// DeployDialog: upload a zip bundle and deploy it from the browser.
//
// Used in two places (contextual buttons): "Deploy new app" (Catalog) and "Deploy new version"
// (AppDetail, with expectedApp set to that app). The zip must be FLAT (index.html + package.json at
// the root); name/version/the atlas manifest are read from package.json (readZipManifest) and sent
// to the existing register API. The backend accepts either tar or zip.
import { useState } from "react";
import type { ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import type { Client, DeployResult } from "../types";
import { MONO } from "../theme";
import { readZipManifest, hasIndexHtml, type ZipManifest } from "../zipManifest";

interface DeployDialogProps {
  client: Client;
  expectedApp?: string; // lock deploys to this app name (new-version flow)
  onClose: () => void;
  onDeployed?: (name: string) => void;
}

export function DeployDialog({ client, expectedApp, onClose, onDeployed }: DeployDialogProps) {
  const [meta, setMeta] = useState<ZipManifest | null>(null); // parsed from the zip's package.json
  const [bytes, setBytes] = useState<Uint8Array | null>(null); // the zip bytes
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<DeployResult | null>(null);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    setMeta(null);
    setBytes(null);
    setDone(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const m = readZipManifest(buf);
      if (!hasIndexHtml(m.entries)) {
        throw new Error("zip has no index.html at its root, so there is nothing to serve");
      }
      if (expectedApp && m.name !== expectedApp) {
        throw new Error(
          `this zip is for '${m.name}', but you're deploying a new version of '${expectedApp}'. ` +
            `Fix the name in package.json.`,
        );
      }
      setBytes(buf);
      setMeta(m);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function deploy() {
    if (!meta || !bytes) return;
    setBusy(true);
    setError(null);
    setLog([]);
    try {
      const res = await client.deploy(
        { name: meta.name, version: meta.version, manifest: meta.manifest, bytes },
        { onStep: (m) => setLog((l) => [...l, m]) },
      );
      setDone(res);
      if (onDeployed) onDeployed(meta.name);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box>
          <Typography variant="h4" component="h1">
            {expectedApp ? `Deploy new version of ${expectedApp}` : "Deploy a new app"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.85, maxWidth: "62ch" }}>
            Validate a production bundle and send it through the Atlas registration pipeline.
          </Typography>
        </Box>
        <Button startIcon={<CloseIcon />} onClick={onClose} disabled={busy} color="inherit">
          Close
        </Button>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderStyle: "dashed",
          textAlign: "center",
          bgcolor: "grey.50",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <UploadFileIcon />
          </Box>
          <Box>
            <Typography variant="h6">Choose a production zip</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 650 }}>
              Zip the contents of the output folder. Keep <code>index.html</code>,{" "}
              <code>package.json</code>, and assets at the archive root.
            </Typography>
          </Box>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
            Browse files
            <input type="file" accept=".zip,application/zip" hidden onChange={onFile} disabled={busy} />
          </Button>
          <Typography variant="caption" color="text.secondary">
            App name, version, and manifest are read from package.json.
          </Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {meta && !done && (
        <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CheckCircleOutlineIcon color="success" />
              <Box>
                <Typography fontWeight={600}>
                  {meta.name} <Box component="span" sx={{ color: "text.secondary" }}>v{meta.version}</Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {expectedApp
                    ? "The archive is valid and ready to deploy."
                    : "New apps enter the review queue before going live."}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={deploy} disabled={busy}>
                {busy ? "Deploying..." : "Deploy bundle"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {log.length > 0 && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            borderRadius: 2,
            bgcolor: "#0d1117",
            color: "#c9d1d9",
            fontFamily: MONO,
            fontSize: 13,
            whiteSpace: "pre-wrap",
          }}
        >
          {log.map((l) => `> ${l}`).join("\n")}
        </Box>
      )}

      {done && (
        <Alert severity="success">
          {done.name} v{done.version} deployed ({done.status}).{" "}
          {expectedApp
            ? "The new version is live."
            : "If this was a brand-new app, it is awaiting review before it serves."}
        </Alert>
      )}
    </Stack>
  );
}
